import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import { AGENTS, WELCOME_MESSAGES } from './constants';
import MessageRenderer from './MessageRenderer';
import {
  QuizEditable,
  FillTheBoxEditable,
  SelectGroupEditable,
  MatchPairsEditable,
  RearrangeEditable,
  CrosswordEditable
} from './GameComponents';

import {
  isQuizMessage,
  parseQuiz,
  isFillTheBoxMessage,
  parseFillTheBox,
  isSelectGroupMessage,
  parseSelectGroup,
  isMatchPairsMessage,
  parseMatchPairs,
  isRearrangeMessage,
  parseRearrange,
  isCrosswordMessage,
  parseCrossword,
  isYoutubeMessage,
  isWritingMessage
} from '../utils/gameMessageUtils';
import SchoolIcon from '@mui/icons-material/School';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { keyframes } from '@emotion/react';

// Animazione "pulse" stile AgentStart
const pulseBorder = keyframes`
  0% {
    box-shadow: 0 0 0 4px rgba(223,70,52,0.5);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(223,70,52,0.2);
  }
  100% {
    box-shadow: 0 0 0 4px rgba(223,70,52,0.5);
  }
`;

// Animazione per il menu
const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Componente che mostra il dettaglio della chat quando si clicca sulla penna
 */
const GlobalChatDetail = ({
  open,
  onClose,
  selectedChat,
  lastAssistantMessage,
  editMode,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  editedText,
  onChangeEditedText,
  handleEditableUpdate,
  newUserMessage,
  onChangeNewUserMessage,
  generating,
  onSendMessage,
  detailLoading,
  // Nuove props per il profilo
  profile,
  profileLoading,
}) => {
  // Funzione per verificare se il messaggio è di un tipo che non può essere modificato
  const isNotEditableMessage = () => {
    if (!lastAssistantMessage) return false;
    return isYoutubeMessage(lastAssistantMessage) || isCrosswordMessage(lastAssistantMessage) || isFillTheBoxMessage(lastAssistantMessage);
  };

  // Reset del messaggio utente quando si apre il modal
  useEffect(() => {
    if (open) {
      onChangeNewUserMessage('');
    }
  }, [open, onChangeNewUserMessage]);

  // Stato per il selettore classe
  const [classMenuOpen, setClassMenuOpen] = useState(false);
  const classSelectorRef = useRef(null);
  const [selectedClass, setSelectedClass] = useState(""); // Classe selezionata attualmente
  const [availableClasses, setAvailableClasses] = useState([]); // Array di classi disponibili

  // Funzione che restituisce l'elenco delle classi in base al tipo di scuola
  const getClassesBySchoolType = (schoolType) => {
    if (!schoolType) return [];
    
    switch (schoolType.toLowerCase()) {
      case 'infanzia':
        return ['prima', 'seconda', 'terza'];
      case 'primaria':
        return ['prima', 'seconda', 'terza', 'quarta', 'quinta'];
      case 'secondaria di primo grado':
      case 'media':
      case 'secondaria i grado':
        return ['prima', 'seconda', 'terza'];
      default:
        return [];
    }
  };

  // Effetto per caricare la classe selezionata dal localStorage al caricamento del componente
  useEffect(() => {
    const savedClass = localStorage.getItem('selectedClass');
    if (savedClass) {
      setSelectedClass(savedClass);
    }
  }, []);
  
  // Effetto per aggiornare le classi disponibili quando cambia il profilo
  useEffect(() => {
    if (profile && !profileLoading) {
      // Determina il tipo di scuola e genera tutte le classi disponibili
      const schoolType = profile.school || "";
      const allClassesForSchool = getClassesBySchoolType(schoolType);
      
      // Se abbiamo classi disponibili per questo tipo di scuola
      if (allClassesForSchool.length > 0) {
        setAvailableClasses(allClassesForSchool);
        
        // Se non è stata selezionata alcuna classe, prova a usare quella dal profilo o la prima disponibile
        if (!selectedClass) {
          // Estrai le classi dal profilo e rimuovi eventuali spazi alla fine
          const profileClasses = profile.current_class_type
            ? profile.current_class_type.split(',').map(c => c.trim()).filter(Boolean)
            : [];
          
          // Trova la prima classe dal profilo che è valida per questo tipo di scuola
          const validProfileClass = profileClasses.find(cls => 
            allClassesForSchool.includes(cls.toLowerCase())
          );
          
          // Usa la classe dal profilo se valida, altrimenti usa la prima disponibile
          const classToSelect = validProfileClass || allClassesForSchool[0];
          setSelectedClass(classToSelect);
          localStorage.setItem('selectedClass', classToSelect);
        }
      }
    }
  }, [profile, profileLoading, selectedClass]);

  // Gestisce il cambio della classe selezionata
  const handleClassChange = (newClass) => {
    setSelectedClass(newClass);
    localStorage.setItem('selectedClass', newClass);
    setClassMenuOpen(false);
  };

  // Apre/chiude il menu di selezione classe
  const toggleClassMenu = () => {
    setClassMenuOpen(prev => !prev);
  };

  // Click all'esterno per chiudere il menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (classSelectorRef.current && !classSelectorRef.current.contains(event.target)) {
        setClassMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Estraggo informazioni dal profilo
  const getProfileInfo = () => {
    if (!profile || profileLoading) return null;
    
    const scuola = profile.school || "";
    let classe = selectedClass || ""; // Usiamo la classe selezionata
    
    if (scuola && classe) {
      return { scuola, classe };
    }
    
    return null;
  };
  
  const profileInfo = getProfileInfo();
  const isMobile = window.innerWidth <= 600;

  // Wrapper della funzione onSendMessage per aggiungere info sulla classe
  const handleSendMessageWithClassInfo = (e) => {
    e.preventDefault();
    if (!newUserMessage.trim()) return;
    
    // Preparare le informazioni della classe
    let classeInfo = "";
    if (profile && !profileLoading && selectedClass) {
      const scuola = profile.school || "";
      if (scuola && selectedClass) {
        classeInfo = `!!!#Falla per classe: ${scuola} ${selectedClass}!!!#`;
      }
    }
    
    // Se c'è un'informazione sulla classe, aggiungerla al messaggio originale
    if (classeInfo) {
      // Creiamo una copia dell'evento per evitare di modificare l'originale
      const syntheticEvent = {
        ...e,
        preventDefault: () => {},
      };
      
      // Memorizza il messaggio originale da mostrare all'utente
      const originalMessage = newUserMessage;
      
      // Creiamo un messaggio temporaneo che includa le informazioni sulla classe
      const messageWithClassInfo = `${originalMessage}\n${classeInfo}`;
      
      // Chiamiamo direttamente onSendMessage ma internamente modifichiamo il messaggio
      // In questo modo la chiamata API invierà il messaggio con le info sulla classe
      const originalOnSendMessage = onSendMessage;
      onSendMessage = (evt) => {
        // Sovrascriviamo temporaneamente la funzione di onChange per impostare il messaggio con la classe
        const originalOnChange = onChangeNewUserMessage;
        onChangeNewUserMessage(messageWithClassInfo);
        
        // Chiamiamo la funzione originale
        originalOnSendMessage(evt);
        
        // Ripristiniamo la funzione originale e il messaggio originale
        onSendMessage = originalOnSendMessage;
        onChangeNewUserMessage = originalOnChange;
      };
      
      // Triggeriamo l'invio
      onSendMessage(syntheticEvent);
      
      // Resettiamo l'input (non serve chiamare onChangeNewUserMessage perché
      // l'originale onSendMessage lo fa già)
      return;
    }
    
    // Se non c'è info sulla classe, chiamiamo direttamente la funzione originale
    onSendMessage(e);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '900px',
            height: '80%',
            bgcolor: 'background.paper',
            border: '2px solid #ddd',
            boxShadow: 24,
            borderRadius: '1rem',
            display: 'flex',
            flexDirection: 'column',
            p: 2
          }}
        >
          {/* Header modal */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {AGENTS.find(a => a.key === selectedChat?.agent)?.label || selectedChat?.agent.toUpperCase()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {lastAssistantMessage && !editMode && !isNotEditableMessage() && (
                <Button onClick={onStartEdit} variant="text" sx={{ 
                  borderRadius: '1rem',
                  color: '#757575'
                }}>
                  Modifica
                </Button>
              )}
              {editMode && (
                <>
                  <Button onClick={onSaveEdit} variant="contained" sx={{ 
                    borderRadius: '1rem',
                    backgroundColor: '#757575',
                    '&:hover': {
                      backgroundColor: '#616161'
                    }
                  }}>
                    Salva
                  </Button>
                  <Button onClick={onCancelEdit} variant="outlined" sx={{ 
                    borderRadius: '1rem',
                    color: '#757575',
                    borderColor: '#757575',
                    '&:hover': {
                      borderColor: '#616161',
                      backgroundColor: 'rgba(117, 117, 117, 0.04)'
                    }
                  }}>
                    Annulla
                  </Button>
                </>
              )}
              <Button onClick={onClose} variant="outlined" sx={{ 
                borderRadius: '1rem',
                color: '#757575',
                borderColor: '#757575',
                '&:hover': {
                  borderColor: '#616161',
                  backgroundColor: 'rgba(117, 117, 117, 0.04)'
                }
              }}>
                Chiudi
              </Button>
            </Box>
          </Box>

          {/* Contenuto principale: ultima risposta e form di input */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {/* SPINNER mentre si caricano i dettagli */}
            {detailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress sx={{ color: '#DF4634' }} />
              </Box>
            ) : (
              <>
                {/* SPINNER mentre GPT elabora */}
                {generating && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      color: 'red',
                      mb: 2,
                    }}
                  >
                    <CircularProgress size={24} sx={{ color: 'red' }} />
                    <Typography sx={{ color: 'red' }}>
                      Minerva sta creando l'attività
                    </Typography>
                  </Box>
                )}

                {/* ULTIMA RISPOSTA (Assistant) */}
                {lastAssistantMessage ? (
                  <>
                    {!editMode ? (
                      <Box sx={{ mb: 2 }}>
                        {/* Rendering del messaggio "mini" o markdown */}
                        <MessageRenderer message={lastAssistantMessage} chat={selectedChat} />
                      </Box>
                    ) : (
                      <Box sx={{ mb: 2 }}>
                        {/* Verifica il tipo di messaggio e visualizza il componente di editing appropriato */}
                        {isQuizMessage(lastAssistantMessage) ? (
                          <QuizEditable 
                            quizData={parseQuiz(lastAssistantMessage)} 
                            onUpdate={handleEditableUpdate} 
                          />
                        ) : isFillTheBoxMessage(lastAssistantMessage) ? (
                          <FillTheBoxEditable 
                            fillData={parseFillTheBox(lastAssistantMessage)} 
                            onUpdate={handleEditableUpdate} 
                          />
                        ) : isSelectGroupMessage(lastAssistantMessage) ? (
                          <SelectGroupEditable 
                            data={parseSelectGroup(lastAssistantMessage)} 
                            onUpdate={handleEditableUpdate} 
                          />
                        ) : isMatchPairsMessage(lastAssistantMessage) ? (
                          <MatchPairsEditable 
                            data={parseMatchPairs(lastAssistantMessage)} 
                            onUpdate={handleEditableUpdate} 
                          />
                        ) : isRearrangeMessage(lastAssistantMessage) ? (
                          <RearrangeEditable 
                            data={parseRearrange(lastAssistantMessage)} 
                            onUpdate={handleEditableUpdate} 
                          />
                        ) : isCrosswordMessage(lastAssistantMessage) ? (
                          <CrosswordEditable 
                            data={parseCrossword(lastAssistantMessage)} 
                            onUpdate={handleEditableUpdate} 
                          />
                        ) : (
                          /* Per gli altri tipi di messaggi, usa ancora il TextField */
                          <TextField
                            multiline
                            rows={6}
                            value={editedText}
                            onChange={(e) => onChangeEditedText(e.target.value)}
                            fullWidth
                            variant="outlined"
                            sx={{ 
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '1rem'
                              }
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: 'rgba(223, 70, 52, 0.05)', 
                    borderRadius: '1rem',
                    border: '1px dashed #DF4634'
                  }}>
                    <Typography sx={{ whiteSpace: 'pre-line' }}>
                      {WELCOME_MESSAGES[selectedChat?.agent] || 'Seleziona un tipo di gioco per iniziare.'}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Form di input per l'utente */}
          {/* Mostra il form solo se NON è una chat di scrittura con un video YouTube come ultimo messaggio */}
          {!(selectedChat?.agent === 'scrittura' && lastAssistantMessage && isYoutubeMessage(lastAssistantMessage)) && (
            <Box sx={{ borderTop: '1px solid #ddd', pt: 2, position: 'relative' }}>
              {/* SELETTORE DI CLASSE */}
              {profileInfo && (
                <Box 
                  ref={classSelectorRef}
                  sx={{ 
                    position: 'absolute', 
                    top: isMobile ? '-2.5rem' : '-2.5rem', 
                    right: isMobile ? '0.5rem' : '0rem',
                    zIndex: 50,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                >
                  {/* Pill button */}
                  <Button
                    onClick={toggleClassMenu}
                    startIcon={<SchoolIcon />}
                    endIcon={<KeyboardArrowDownIcon 
                      sx={{
                        transform: classMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />}
                    sx={{
                      backgroundColor: classMenuOpen ? '#c13c2b' : '#DF4634',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: isMobile ? '0.65rem' : '0.75rem',
                      padding: isMobile ? '2px 10px' : '4px 14px',
                      height: isMobile ? '26px' : '32px',
                      minWidth: 'auto',
                      borderRadius: '20px',
                      textTransform: 'none',
                      boxShadow: classMenuOpen ? '0 4px 6px rgba(0,0,0,0.25)' : '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s ease',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: '#c13c2b',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        transform: 'translateY(-2px)',
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: '4px',
                        '& svg': {
                          fontSize: isMobile ? '0.9rem' : '1.1rem',
                        }
                      },
                      '& .MuiButton-endIcon': {
                        marginLeft: '2px',
                        '& svg': {
                          fontSize: isMobile ? '0.9rem' : '1.1rem',
                        }
                      },
                    }}
                  >
                    {`${profileInfo.scuola} ${profileInfo.classe}`}
                  </Button>

                  {/* Menu a tendina */}
                  {classMenuOpen && (
                    <Box
                      sx={{
                        mb: 0.5,
                        position: 'absolute',
                        bottom: '100%',
                        right: 0,
                        borderRadius: '15px',
                        boxShadow: '0 -5px 25px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                        width: isMobile ? '160px' : '220px',
                        backgroundColor: 'white',
                        animation: `${slideDown} 0.2s ease-out forwards`,
                        border: '1px solid rgba(0,0,0,0.08)',
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-8px',
                          right: isMobile ? '32px' : '40px',
                          width: '16px',
                          height: '16px',
                          backgroundColor: 'white',
                          transform: 'rotate(45deg)',
                          borderRight: '1px solid rgba(0,0,0,0.08)',
                          borderBottom: '1px solid rgba(0,0,0,0.08)',
                          zIndex: 0,
                        }
                      }}
                    >
                      <Box sx={{ 
                        p: 1.5, 
                        pb: 0.5,
                        borderBottom: '1px solid rgba(0,0,0,0.06)'
                      }}>
                        <Typography sx={{ 
                          fontSize: isMobile ? '0.7rem' : '0.8rem', 
                          color: 'rgba(0,0,0,0.6)',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Seleziona classe
                        </Typography>
                      </Box>
                      
                      <Box sx={{ maxHeight: 200, overflowY: 'auto', p: 0.5 }}>
                        {availableClasses.map((classe, index) => (
                          <MenuItem
                            key={index}
                            onClick={() => handleClassChange(classe)}
                            sx={{
                              py: 1,
                              px: 1.5,
                              borderRadius: '10px',
                              m: 0.3,
                              minHeight: 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              transition: 'all 0.15s ease',
                              backgroundColor: selectedClass === classe ? 'rgba(223,70,52,0.08)' : 'transparent',
                              '&:hover': {
                                backgroundColor: selectedClass === classe ? 'rgba(223,70,52,0.12)' : 'rgba(0,0,0,0.04)',
                              }
                            }}
                          >
                            {selectedClass === classe ? (
                              <CheckCircleIcon 
                                sx={{ 
                                  fontSize: isMobile ? '0.9rem' : '1.1rem',
                                  mr: 1,
                                  color: '#DF4634'
                                }}
                              />
                            ) : (
                              <Box sx={{ width: isMobile ? '0.9rem' : '1.1rem', mr: 1 }} />
                            )}
                            <Typography 
                              sx={{ 
                                fontWeight: selectedClass === classe ? 600 : 400,
                                fontSize: isMobile ? '0.75rem' : '0.85rem',
                                color: selectedClass === classe ? '#DF4634' : 'rgba(0,0,0,0.75)',
                              }}
                            >
                              {`${profile?.school || ""} ${classe}`}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              <form onSubmit={handleSendMessageWithClassInfo} style={{ display: 'flex', gap: '8px' }}>
                <TextField
                  placeholder="Scrivi la tua richiesta..."
                  fullWidth
                  multiline
                  rows={2}
                  value={newUserMessage}
                  onChange={(e) => onChangeNewUserMessage(e.target.value)}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '1rem',
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused fieldset': { 
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: '1px'
                      }
                    }
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ 
                    width: '120px', 
                    alignSelf: 'flex-end',
                    borderRadius: '1rem',
                    backgroundColor: '#DF4634',
                    '&:hover': {
                      backgroundColor: '#E57373',
                      boxShadow: '0 4px 10px rgba(223, 70, 52, 0.3)',
                    }
                  }}
                  disabled={generating}
                >
                  Invia
                </Button>
              </form>
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default GlobalChatDetail; 